import createClient, { type Middleware } from "openapi-fetch";

import { publicConfig } from "@/config/public-config";

import { ApiError, extractCorrelationId } from "./errors";
import type { paths } from "./schema";

/**
 * Tier the response body extraction so non-JSON error pages (text/html 502 from
 * reverse proxies, plain-text rate-limit messages, etc.) still surface in
 * ApiError.body instead of being silently dropped to null.
 */
async function readErrorBody(response: Response): Promise<unknown> {
  // `clone()` because middleware may run before the caller reads the body.
  const cloned = response.clone();
  const contentType = cloned.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) {
    return await cloned.json().catch(() => null);
  }
  return await cloned.text().catch(() => null);
}

const errorMiddleware: Middleware = {
  async onResponse({ response }) {
    if (!response.ok) {
      const correlationId = extractCorrelationId(response.headers);
      const body = await readErrorBody(response);
      throw new ApiError(response.status, correlationId, body);
    }
    return response;
  },
};

// Reads the JWT from localStorage and attaches it as a Bearer token.
// Key must match token-storage.ts — cannot import from shared/ due to boundary rules.
const authMiddleware: Middleware = {
  async onRequest({ request }) {
    const token = localStorage.getItem("atlas_access_token");
    if (token) {
      request.headers.set("Authorization", `Bearer ${token}`);
    }
    return request;
  },
};

interface ApiClientOptions {
  baseUrl?: string;
}

/**
 * Factory: build an isolated apiClient. Use this in tests to point at MSW
 * fixtures with a different baseUrl, or in features that need to inject
 * additional middleware without mutating the singleton.
 */
export function createApiClient(options: ApiClientOptions = {}) {
  const client = createClient<paths>({ baseUrl: options.baseUrl ?? publicConfig.API_BASE_URL });
  client.use(authMiddleware);
  client.use(errorMiddleware);
  return client;
}

/** Default singleton — uses publicConfig.API_BASE_URL. Tests should prefer createApiClient(). */
export const apiClient = createApiClient();
